import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
  Avatar,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import {
  WhatsApp,
  YouTube,
  Instagram,
  Email,
  MusicNote,
  Phone,
  LocationOn,
} from "@mui/icons-material";

export const Contato = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const contactInfo = [
    {
      icon: <Phone sx={{ color: "primary.main", fontSize: 32 }} />,
      title: "Telefone",
      content: "+55 83 8737-6137",
      action: () => window.open("tel:+5583987376137"),
    },
    {
      icon: <Email sx={{ color: "primary.main", fontSize: 32 }} />,
      title: "Email",
      content: "seccampinazonasul@verbodavida.com",
      action: () => window.open("mailto:seccampinazonasul@verbodavida.com"),
    },
    {
      icon: <LocationOn sx={{ color: "primary.main", fontSize: 32 }} />,
      title: "Endereço",
      content: "Campina Grande - PB",
      action: () => {},
    },
  ];

  const socialLinks = [
    {
      icon: <WhatsApp sx={{ fontSize: { xs: 32, md: 40 }, color: "#25D366" }} />,
      title: "WhatsApp",
      description: "Fale conosco pelo WhatsApp",
      link: "https://wa.me/5583987376137",
      color: "#25D366",
      buttonColor: "success",
    },
    {
      icon: <Instagram sx={{ fontSize: { xs: 32, md: 40 }, color: "#E4405F" }} />,
      title: "Instagram",
      description: "Siga-nos no Instagram",
      link: "https://www.instagram.com/verbozonasulcg/",
      color: "#E4405F",
      buttonColor: "secondary",
    },
    {
      icon: <MusicNote sx={{ fontSize: { xs: 32, md: 40 }, color: "#1DB954" }} />,
      title: "Spotify",
      description: "Ouça nossos podcasts",
      link: "https://open.spotify.com/show/0VX4JuFLeDCinv6n9CtDk4?si=7970245ef4f44752",
      color: "#1DB954",
      buttonColor: "success",
    },
  ];

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f8f9fa", 
        minHeight: "100vh",
        pt: { xs: "80px", md: "100px" },
        pb: { xs: 4, md: 6 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Container maxWidth="lg" sx={{ width: "100%" }}>
        {/* Cabeçalho */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 }, mx: "auto" }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Fale Conosco
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
              px: { xs: 2, md: 0 },
            }}
          >
            Estamos sempre prontos para ouvir você. Entre em contato através de nossos canais de comunicação.
          </Typography>
        </Box>

        {/* Informações de Contato */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 4, md: 6 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ maxWidth: "900px", justifyContent: "center" }}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: { xs: "240px", md: "260px" },
                    width: { xs: "280px", md: "280px" },
                    textAlign: "center",
                    cursor: info.action ? "pointer" : "default",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: info.action ? "translateY(-4px)" : "none",
                      boxShadow: info.action ? "0 4px 20px rgba(0,0,0,0.15)" : "0 2px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={info.action}
                >
                  <CardContent sx={{ 
                    p: { xs: 3, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}>
                    <Avatar
                      sx={{
                        backgroundColor: "rgba(139, 38, 53, 0.1)",
                        width: { xs: 60, md: 70 },
                        height: { xs: 60, md: 70 },
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      {info.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{ 
                        color: "primary.main",
                        fontWeight: "bold", 
                        mb: 1,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                      }}
                    >
                      {info.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        wordBreak: "break-word",
                        textAlign: "center",
                        lineHeight: 1.4,
                      }}
                    >
                      {info.content}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Redes Sociais */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              textAlign: "center",
              mb: { xs: 3, md: 4 },
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Nossas Redes Sociais
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ maxWidth: "900px", justifyContent: "center" }}>
              {socialLinks.map((social, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: { xs: "240px", md: "260px" },
                      width: { xs: "280px", md: "280px" },
                      textAlign: "center",
                      cursor: "pointer",
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      mx: "auto",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      },
                    }}
                    onClick={() => window.open(social.link, "_blank")}
                  >
                    <CardContent sx={{ 
                      p: { xs: 3, md: 4 },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      height: "100%",
                    }}>
                      <Box sx={{ mb: 2 }}>{social.icon}</Box>
                      <Typography
                        variant="h6"
                        sx={{ 
                          color: "primary.main",
                          fontWeight: "bold", 
                          mb: 1,
                          fontSize: { xs: "1.1rem", md: "1.25rem" },
                        }}
                      >
                        {social.title}
                      </Typography>
                      <Box sx={{ flexGrow: 1, mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            lineHeight: 1.4,
                          }}
                        >
                          {social.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color={social.buttonColor}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          fontWeight: "bold",
                          borderRadius: 2,
                          textTransform: "none",
                          px: { xs: 2, md: 3 },
                          mt: "auto",
                        }}
                      >
                        Acessar
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* YouTube */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              textAlign: "center",
              mb: { xs: 3, md: 4 },
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Nosso Canal no YouTube
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<YouTube />}
              onClick={() => window.open("https://www.youtube.com/@verbozonasulcg", "_blank")}
              sx={{
                backgroundColor: "#FF0000",
                color: "white",
                fontWeight: "bold",
                px: { xs: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                borderRadius: 2,
                textTransform: "none",
                fontSize: { xs: "1rem", md: "1.1rem" },
                "&:hover": {
                  backgroundColor: "#cc0000",
                },
              }}
            >
              VER CANAL
            </Button>
          </Box>
        </Box>

        {/* WhatsApp Call to Action */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper
            sx={{
              backgroundColor: "#25D366",
              color: "white",
              p: { xs: 3, md: 4 },
              textAlign: "center",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(37, 211, 102, 0.3)",
              maxWidth: "500px",
              width: "100%",
            }}
          >
            <WhatsApp sx={{ fontSize: { xs: 48, md: 60 }, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ 
                fontWeight: "bold", 
                mb: 2, 
                fontSize: { xs: "1.25rem", md: "1.5rem" } 
              }}
            >
              Precisa falar conosco agora?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 3, 
                opacity: 0.9,
                fontSize: { xs: "0.9rem", md: "1rem" },
                px: { xs: 1, md: 0 },
              }}
            >
              Envie uma mensagem pelo WhatsApp e nossa equipe responderá o mais breve possível.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsApp />}
              onClick={() => window.open("https://wa.me/5583987376137", "_blank")}
              sx={{
                backgroundColor: "white",
                color: "#25D366",
                fontWeight: "bold",
                px: { xs: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                borderRadius: 2,
                textTransform: "none",
                fontSize: { xs: "1rem", md: "1.1rem" },
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
              }}
            >
              Enviar Mensagem
            </Button>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};